import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { FitRing, FitBreakdown } from "./FitVisuals";
import SourcePanel from "./SourcePanel";
import { Loader2, MapPin, TrendingUp, Sparkles, Layers } from "lucide-react";

const payText = (band) => {
  if (!band || band.min == null) return "Not published";
  const unit = band.currency === "INR" ? "L" : "K";
  const prefix = band.currency === "INR" ? "₹" : "$";
  return band.max == null ? `${prefix}${band.min}${unit}+` : `${prefix}${band.min}-${band.max}${unit}`;
};

const RELATION = {
  current: { label: "You are here", cls: "border-sky-400 bg-sky-50 text-slate-900" },
  behind: { label: "Behind you", cls: "border-slate-200 bg-slate-50 text-slate-500" },
  next: { label: "Next rung", cls: "border-emerald-300 bg-emerald-50 text-slate-900" },
  reach: { label: "", cls: "border-slate-200 bg-white text-slate-700" },
};

const Pyramid = ({ title, region, levels, selected, onSelect, payKey }) => (
  <div className="card-soft p-4" data-testid={`pyramid-${region}`}>
    <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{title}</p>
    <div className="mt-3 flex flex-col items-center gap-1.5">
      {[...levels].reverse().map((level) => {
        const tone = RELATION[level.relation] || RELATION.reach;
        const width = 100 - (level.index / Math.max(levels.length - 1, 1)) * 42;
        return (
          <button key={level.index} onClick={() => onSelect(level.index)} style={{ width: `${width}%` }}
            className={`rounded-lg border px-3 py-2 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm ${tone.cls} ${selected === level.index ? "ring-2 ring-slate-900/15" : ""}`}
            data-testid={`pyramid-${region}-level-${level.index}`}>
            <span className="flex items-baseline justify-between gap-2">
              <span className="text-xs font-semibold leading-snug" title={level.title}>{level.title}</span>
              <span className="num-display shrink-0 text-[11px]">{payText(level[payKey])}</span>
            </span>
            {tone.label && <span className="mt-0.5 block text-[10px] uppercase tracking-wider opacity-70">{tone.label}</span>}
          </button>
        );
      })}
    </div>
  </div>
);

export const CareerPyramid = ({ resumeId }) => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [gap, setGap] = useState(null);
  const [gapBusy, setGapBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (trackKey) => {
    try {
      const params = new URLSearchParams();
      if (resumeId) params.set("resume_id", resumeId);
      if (trackKey) params.set("track", trackKey);
      const { data: payload } = await api.get(`/career-ladder?${params.toString()}`);
      setData(payload);
      setGap(null);
      setSelected(payload.current_index >= 0 ? Math.min(payload.current_index + 1, payload.levels.length - 1) : null);
    } catch {
      setFailed(true);
      toast.error("Could not load your career ladder.");
    }
  }, [resumeId]);

  useEffect(() => { load(""); }, [load]);

  const level = data && selected != null ? data.levels[selected] : null;

  const loadGap = async () => {
    const requested = selected;
    setGapBusy(true);
    try {
      const { data: payload } = await api.post(`/career-ladder/${data.track.key}/${requested}/gap?resume_id=${resumeId}`);
      if (requested === selected) setGap(payload.gap);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build your gap note.");
    } finally { setGapBusy(false); }
  };

  if (failed) {
    return (
      <section className="mt-8 card-soft p-4" data-testid="career-pyramid-error">
        <p className="text-sm text-slate-700">We could not load your career ladder just now.</p>
        <Button onClick={() => { setFailed(false); load(""); }} variant="outline" className="mt-2 h-9 rounded-xl border-slate-300 text-sm" data-testid="pyramid-retry">Try again</Button>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="mt-8 card-soft p-4" data-testid="career-pyramid-loading">
        <div className="h-4 w-56 animate-pulse rounded bg-slate-100" />
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {[0, 1].map((col) => (
            <div key={col} className="space-y-2">
              {[0, 1, 2, 3].map((row) => <div key={row} className="mx-auto h-9 animate-pulse rounded-lg bg-slate-100" style={{ width: `${100 - row * 12}%` }} />)}
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="mt-8" data-testid="career-pyramid">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg"><Layers className="h-4 w-4 text-sky-600" /> Where you stand, and what is above you</h2>
          <p className="mt-1 text-sm text-slate-600">{data.track.note || "The rungs on your track, in India and globally."}</p>
          {data.placed_by && <p className="mt-1 text-[11px] text-slate-500" data-testid="pyramid-placed-by">{data.placed_by}.</p>}
        </div>
        <label className="text-[11px] uppercase tracking-wider text-slate-500">Track
          <select value={data.track.key} onChange={(e) => load(e.target.value)}
            className="mt-1 block rounded-xl border border-slate-200 bg-white px-3 py-2 font-sans text-sm text-slate-900" data-testid="pyramid-track-select">
            {data.tracks.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </label>
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <Pyramid title="India · annual CTC" region="india" levels={data.levels} selected={selected} onSelect={(i) => { setSelected(i); setGap(null); }} payKey="india" />
        <Pyramid title="Global · annual total (USD)" region="global" levels={data.levels} selected={selected} onSelect={(i) => { setSelected(i); setGap(null); }} payKey="global" />
      </div>

      {level && (
        <div className="mt-3 card-soft p-4 sm:p-5" data-testid="pyramid-snapshot">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-mono uppercase tracking-wider text-sky-700">{RELATION[level.relation]?.label || "Where you could go"}</p>
              <h3 className="mt-0.5 text-lg font-bold text-slate-900">{level.title}</h3>
              <p className="mt-1 text-sm text-slate-600">{level.separator}</p>
            </div>
            {level.fit && <FitRing score={level.fit.score} verdict={level.fit.verdict} testId={`pyramid-fit-${level.index}`} />}
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="accent-bar rounded-xl border border-slate-200 bg-white p-3 pl-4" style={{ "--accent": "#0D9488" }}>
              <p className="text-[10px] font-mono uppercase tracking-wider text-teal-700">India pay</p>
              <p className="num-display mt-1 text-base text-slate-900">{payText(level.india)}</p>
              <p className="mt-0.5 text-[11px] text-slate-500">per annum · {level.years_min}+ years typical</p>
            </div>
            <div className="accent-bar rounded-xl border border-slate-200 bg-white p-3 pl-4" style={{ "--accent": "#0284C7" }}>
              <p className="text-[10px] font-mono uppercase tracking-wider text-sky-700">Global pay</p>
              <p className="num-display mt-1 text-base text-slate-900">{payText(level.global)}</p>
              <p className="mt-0.5 text-[11px] text-slate-500">US, UK, EU and Gulf benchmark</p>
            </div>
            <div className="accent-bar rounded-xl border border-slate-200 bg-white p-3 pl-4" style={{ "--accent": "#D97706" }}>
              <p className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-amber-700"><TrendingUp className="h-3 w-3" /> Demand today</p>
              <p className="mt-1 text-xs text-slate-700">{level.demand}</p>
            </div>
            <div className="accent-bar rounded-xl border border-slate-200 bg-white p-3 pl-4" style={{ "--accent": "#7C3AED" }}>
              <p className="text-[10px] font-mono uppercase tracking-wider text-violet-700">Relevance ahead</p>
              <p className="mt-1 text-xs text-slate-700">{level.future}</p>
            </div>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500"><MapPin className="h-3 w-3" /> Where these jobs are</p>
              <p className="mt-1.5 text-xs text-slate-700"><span className="font-semibold text-slate-900">Sectors:</span> {level.sectors.join(" · ")}</p>
              <p className="mt-1 text-xs text-slate-700"><span className="font-semibold text-slate-900">India:</span> {level.hubs_india.join(" · ")}</p>
              <p className="mt-1 text-xs text-slate-700"><span className="font-semibold text-slate-900">Global:</span> {level.hubs_global.join(" · ")}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">What this level expects</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {level.key_skills.map((skill) => {
                  const have = (level.fit?.matched_skills || []).includes(skill);
                  return <span key={skill} className={`rounded-full border px-2 py-0.5 text-[11px] ${have ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600"}`}>{skill}</span>;
                })}
              </div>
            </div>
          </div>

          {level.fit && (
            <div className="mt-3">
              <FitBreakdown fit={level.fit} />
            </div>
          )}

          {resumeId && (
            <div className="mt-3">
              {gap ? (
                <div className="accent-bar rounded-xl border border-slate-200 bg-white p-4 pl-5" style={{ "--accent": "#0284C7" }} data-testid="pyramid-gap">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-sky-700">Your personal gap to this level</p>
                  <p className="mt-1.5 text-sm text-slate-800">{gap.where_you_stand}</p>
                  {gap.what_is_missing?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {gap.what_is_missing.map((item, i) => <li key={i} className="flex gap-2 text-xs text-slate-700"><span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-rose-300" />{item}</li>)}
                    </ul>
                  )}
                  {gap.first_moves?.length > 0 && (
                    <>
                      <p className="mt-3 text-[10px] font-mono uppercase tracking-wider text-slate-500">First moves, next 90 days</p>
                      <ol className="mt-1 space-y-1">
                        {gap.first_moves.map((item, i) => <li key={i} className="flex gap-2 text-xs text-slate-700"><span className="num-display text-slate-400">{i + 1}</span>{item}</li>)}
                      </ol>
                    </>
                  )}
                  {gap.time_estimate && <p className="mt-2 text-[11px] text-slate-500">Realistic timeline: {gap.time_estimate}</p>}
                </div>
              ) : (
                <Button onClick={loadGap} disabled={gapBusy} className="h-10 rounded-xl bg-slate-900 px-4 text-sm text-white hover:bg-slate-800" data-testid="pyramid-gap-button">
                  {gapBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                  What stands between you and this level
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-3">
        <SourcePanel testId="pyramid-sources" items={[data.pay_note]} label="Pay band basis" />
      </div>
    </section>
  );
};

export default CareerPyramid;
